import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';

import { ticketUpdate } from '~/actions/ticket';
import { checkEsignatureIntegration } from '~/api/integrations';
import { checkEnvelope } from '~/api/tickets';
import { Button } from '~/eds/src/components/Button/Button';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { api } from '~/redux/api/createApi';
import { coerceRtkqError } from '~/redux/api/utils';

const CheckSignatureStatus = ({ ticketId }: { ticketId: string }) => {
  const { toast } = useToast();
  const dispatch = useDispatch();
  const intl = useIntl();

  const [checkSignature, checkSignatureResult] =
    api.endpoints.updateEnvelope.useMutation();

  const { isLoading: isLoadingCheckSignature } = checkSignatureResult;

  const updateTicket = useCallback(async () => {
    const { data: hasEnvelope } = await checkEnvelope(ticketId);
    const hasEsignatureIntegration = await checkEsignatureIntegration();

    dispatch(ticketUpdate({ hasEnvelope, hasEsignatureIntegration }));
  }, [ticketId]);

  const handleCheckSignature = async () => {
    try {
      await checkSignature({ ticketId }).unwrap();
    } catch (checkSignatureError: any) {
      toast({
        autoDismiss: false,
        message:
          coerceRtkqError(checkSignatureError)?.response?.data?.[0]?.detail ||
          coerceRtkqError(checkSignatureError)?.response?.data,
        status: 'warning',
      });
    } finally {
      updateTicket();
    }
  };

  return (
    <Tooltip
      tooltip={intl.formatMessage({
        id: 'CheckSignatureStatus.tooltip',
        defaultMessage: 'Check for E-Signature status updates',
        description: 'Tooltip for the check signature status button.',
      })}
    >
      <Button
        text={intl.formatMessage({
          id: 'CheckSignatureStatus.buttonText',
          defaultMessage: 'Check Signature Status',
          description: 'Button text to check e-signature status.',
        })}
        variant="primary"
        onClick={handleCheckSignature}
        isLoading={isLoadingCheckSignature}
      />
    </Tooltip>
  );
};

const mapStateToProps = ({ ticket }: { ticket: any }) => {
  return {
    ticketId: ticket.id,
  };
};

export default connect(mapStateToProps)(CheckSignatureStatus);
